import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { SERIES, isoDuration, journeyOrdered } from './tutorialConfigs';
import TUTORIAL_TRANSCRIPTS from './tutorialTranscripts';

/**
 * tutorialSchema — JSON-LD generators for /tutorials, per Rhea's CEL-1156
 * ruling (design doc §6.2/§7.2, R1):
 *  - Per video page: VideoObject + BreadcrumbList. Deliberate REFUSALS:
 *    NO Clip, NO SeekToAction, NO FAQPage, NO embedUrl (self-hosted
 *    contentUrl only; the YT lane is fully decoupled). The hero schemas on
 *    home//ai-employees keep their embedUrl/SeekToAction shape — Rhea's
 *    refusals are tutorial-scoped and do not retro-apply.
 *  - Hub: CollectionPage + ItemList of LIVE video-page URLs only (D4).
 *  - uploadDate = the date the PAGE went live (config `uploadDate`, filled at
 *    publish), never the cut's render date.
 */

const absUrl = (path) => `${SEO_DEFAULTS.url}${path}`;

const PUBLISHER = {
  '@type': 'Organization',
  name: 'CellCog',
  logo: {
    '@type': 'ImageObject',
    url: `${SEO_DEFAULTS.url}/logo512.png`,
  },
};

/** Plain-text transcript for the VideoObject `transcript` property. */
const transcriptText = (slug) => {
  const rows = TUTORIAL_TRANSCRIPTS[slug];
  if (!rows || rows.length === 0) return undefined;
  return rows.map((r) => r.text).join(' ');
};

export const generateTutorialVideoSchema = (t) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: t.title,
  description: t.description,
  thumbnailUrl: [absUrl(t.thumbnail)],
  uploadDate: t.uploadDate,
  duration: isoDuration(t.durationSeconds),
  contentUrl: absUrl(t.mp4),
  transcript: transcriptText(t.slug),
  isFamilyFriendly: true,
  inLanguage: 'en',
  publisher: PUBLISHER,
});

export const generateTutorialBreadcrumbSchema = (t) => {
  const series = SERIES.find((s) => s.id === t.series);
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Tutorials',
        item: `${SEO_DEFAULTS.url}/tutorials/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: series ? series.label : t.series,
        item: `${SEO_DEFAULTS.url}/tutorials/#${t.series}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t.title,
        item: `${SEO_DEFAULTS.url}/tutorials/${t.slug}/`,
      },
    ],
  };
};

/** CollectionPage + ItemList — LIVE entries only, journey order (R1/D4). */
export const generateTutorialsHubSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'CellCog Tutorials',
  description:
    'Short tutorials filmed in the real product. Watch a business hire an AI employee, brief it, and work with it, one feature at a time.',
  url: `${SEO_DEFAULTS.url}/tutorials/`,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: journeyOrdered().map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: t.title,
      url: `${SEO_DEFAULTS.url}/tutorials/${t.slug}/`,
    })),
  },
});
